import { notifications, Notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { tr } from '../lib/i18n';

type Kind = 'success' | 'error' | 'info' | 'warning';
interface ToastApi {
  toast: (message: string, kind?: Kind) => void;
  toastError: (err: unknown) => void;
}
const Ctx = createContext<ToastApi | null>(null);

const STYLE: Record<Kind, { color: string; icon: ReactNode }> = {
  success: { color: 'teal', icon: <IconCheck size={16} /> },
  error: { color: 'red', icon: <IconX size={16} /> },
  warning: { color: 'orange', icon: <IconAlertTriangle size={16} /> },
  info: { color: 'blue', icon: <IconInfoCircle size={16} /> },
};

/** Same API as before (toast / toastError), shown as Mantine notifications at the bottom. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const toast = useCallback((text: string, kind: Kind = 'success') => {
    notifications.show({ message: text, ...STYLE[kind], autoClose: kind === 'error' ? 6000 : 3500, withBorder: true, radius: 'md' });
  }, []);
  const toastError = useCallback((err: unknown) => {
    if (err instanceof ApiError) {
      const detail = err.details.find((d) => d.message && d.message !== err.message)?.message;
      toast(detail ? `${err.message} — ${detail}` : err.message, 'error');
    } else toast(err instanceof Error ? err.message : tr('حدث خطأ', 'An error occurred'), 'error');
  }, [toast]);
  const api = useMemo(() => ({ toast, toastError }), [toast, toastError]);
  return (
    <Ctx.Provider value={api}>
      <Notifications position="bottom-center" limit={3} zIndex={3000} />
      {children}
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
